import {
  Box,
  Center,
  Flex,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CiCircleCheck, CiCirclePlus } from 'react-icons/ci';

import ImportedContentSearchFilterMenu from '@/client/components/admin/create/content/forms/tracks/imported-content/ImportedContentSearchFilterMenu';
import { MutableRefObject } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import { learnV4ApiClient } from '@/client/services/api/clients/learnV4ApiClient';
import { useContentSearch } from './useContentSearch';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface ContentSearchProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  selectedContentType: {
    name: string;
    value: string | null;
  } | null;
  setSelectedContentType: (value: { name: string; value: string | null }) => void;
  checkItemIsSelected: (item: any) => boolean;
  handleItemClick: (item: any, isSelected?: boolean | undefined) => void;
  initialInputRef?: MutableRefObject<HTMLInputElement | null>;
  includeAdvancedContentTypes?: boolean;
  workshopsOnly?: boolean;
}

export default function ContentSearch({
  searchTerm,
  selectedContentType,
  setSelectedContentType,
  setSearchTerm,
  initialInputRef,
  checkItemIsSelected,
  handleItemClick,
  includeAdvancedContentTypes = false,
  workshopsOnly = false,
}: ContentSearchProps) {
  const { t } = useTranslation();
  const { contentTypeOptions, contentTypeMap } = useContentSearch(
    includeAdvancedContentTypes,
    workshopsOnly,
  );

  const { data, isFetching } = useQuery({
    queryKey: ['track-existing-content-search', selectedContentType?.value, searchTerm],
    queryFn: async () => {
      const response = await learnV4ApiClient.get('admin-content/analytics', {
        params: {
          $or: `10,title,$regex,2,${searchTerm},i,name,$regex,2,${searchTerm},i`,
          page: '1',
          per_page: '20',
          archived: '$ne,true',
          sort: 'createdAt,-1',
          published_only: true,
          manager_priority: true,
          types: selectedContentType?.value,
        },
      });

      return response.data.data.map((item: any) => ({ id: item._id, ...item }));
    },
  });

  return (
    <>
      <Stack>
        <InputGroup>
          <InputLeftElement>
            <SearchIcon color="neutral.500" />
          </InputLeftElement>

          <Input
            ref={initialInputRef}
            variant="create"
            placeholder={`${t('global.forms.labels_search')}`}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <ImportedContentSearchFilterMenu
          label="Filter"
          options={contentTypeOptions}
          selected={selectedContentType}
          setSelected={setSelectedContentType}
        />
      </Stack>

      {!isFetching && data && data.length > 0 && (
        <Stack marginTop={10}>
          {data.map((item: any, index: number) => {
            const isSelected = checkItemIsSelected(item);

            return (
              <Box
                key={item.id}
                borderBottom={index === data.length - 1 ? 'none' : '1px solid'}
                borderBottomColor="neutral.100"
                paddingY={2}
              >
                <Flex
                  padding={4}
                  border="2px solid"
                  borderColor={isSelected ? 'warmNeutral.200' : 'transparent'}
                  borderRadius="6px"
                  alignItems="center"
                  justifyContent="space-between"
                  _hover={{
                    backgroundColor: 'warmNeutral.0',
                    cursor: 'pointer',
                    borderColor: 'warmNeutral.200',
                  }}
                  onClick={() => handleItemClick(item, isSelected as boolean)}
                >
                  <Flex>
                    <Box
                      minHeight="60px"
                      minWidth="120px"
                      height="60px"
                      width="120px"
                      borderRadius="6px"
                      overflow="hidden"
                      backgroundColor="warmNeutral.200"
                      marginRight={6}
                    >
                      {item.image && <Image src={item.image} />}
                    </Box>

                    <Stack>
                      <Text color="warmNeutral.1000" fontWeight={600} fontSize="14px">
                        {contentTypeMap[item.type]}
                      </Text>

                      <Text wordBreak="break-word">{item.title || item.name}</Text>
                    </Stack>
                  </Flex>

                  <Icon
                    boxSize={8}
                    as={isSelected ? CiCircleCheck : CiCirclePlus}
                    color={isSelected ? 'warmNeutral.500' : 'neutral.600'}
                  />
                </Flex>
              </Box>
            );
          })}
        </Stack>
      )}

      {isFetching && (
        <Center marginTop={10}>
          <Spinner color="warmNeutral.500" />
        </Center>
      )}

      {data && data.length === 0 && (
        <Center marginTop={10}>
          <Text>{t('global.forms.labels_noResults')}</Text>
        </Center>
      )}
    </>
  );
}
